import { type ReactNode, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { restoreFocus } from '@/shared/lib/restore-focus';

import styles from './Dialog.module.css';

interface DialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: 'sm' | 'lg' | 'xl';
  /** Keep the title for screen readers only (content shows an editable title). */
  hideTitle?: boolean;
  /**
   * Actions pinned under the body: with it the dialog is a header, a body that scrolls on its own
   * and this footer - none of them leaves the viewport, however tall the body is.
   */
  footer?: ReactNode;
}

/**
 * Modal dialog on the native `<dialog>` element: focus trapping, Escape and
 * the top layer come from the browser. Always mounted open; unmount to close.
 */
export function Dialog({
  title,
  onClose,
  children,
  size = 'sm',
  hideTitle = false,
  footer,
}: DialogProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // Unmounting (instead of dialog.close()) does not give focus back: do it here.
    // (Captured once: React's dev double-run would otherwise see focus already inside the dialog.)
    const active = document.activeElement;
    if (!opener.current && active instanceof HTMLElement && !dialog.contains(active)) {
      opener.current = active;
    }
    // jsdom (tests) has no showModal().
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    // The field that asked for autofocus gets it once the dialog is open: React's own autoFocus
    // runs before `showModal()`, which then focuses the first control (the close button).
    dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus({ preventScroll: true });
    return () => {
      if (opener.current?.isConnected) restoreFocus(opener.current);
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={`${styles.dialog} ${styles[size]} ${footer ? styles.split : ''}`}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose(); // backdrop click
      }}
    >
      <div className={styles.content}>
        <header className={`${styles.header} ${hideTitle ? styles.headerFloating : ''}`}>
          <h2
            id={titleId}
            className={hideTitle ? 'visually-hidden' : styles.title}
            {...(!hideTitle && { title })}
          >
            {title}
          </h2>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </header>
        {footer ? (
          <>
            <div className={styles.body}>{children}</div>
            <div className={styles.footer}>{footer}</div>
          </>
        ) : (
          children
        )}
      </div>
    </dialog>
  );
}
